import { FunctionComponent } from 'react';
import {
  defineMessages,
  FormattedDate,
  FormattedMessage,
} from 'react-intl-next';
import { Text } from '@atlaskit/primitives';

const messages = defineMessages({
  nullReleaseDate: {
    id: 'appUpdates.dateRangeInfo.noTimeframe',
    defaultMessage: 'No timeframe yet',
    description: 'Text shown when no release dates are defined',
  },
  startingIn: {
    id: 'appUpdates.dateRangeInfo.startingIn',
    defaultMessage: 'Starting in {startingDate}',
    description: 'Release window when only a start date is known, no day shown',
  },
  dateRange: {
    id: 'appUpdates.dateRangeInfo.dateRange',
    defaultMessage: '{dateStart} - {dateEnd}',
    description: 'Release window date range, e.g. "Mar - Apr 2024"',
  },
});

export interface DateRangeInfoProps {
  /**
   * ISO date string for the start of the date range
   */
  startDate?: string | null;
  /**
   * ISO date string for the end of the date range
   */
  endDate?: string | null;
  /**
   * Release status. When 'GENERALLY_AVAILABLE', only the end date is shown
   * since the start date is no longer relevant.
   */
  status?: string | null;
}

/**
 * Returns true if two ISO date strings fall in the same calendar month.
 */
export const inSameMonth = (start: string, end: string): boolean => {
  const month1 = new Date(start).getMonth();
  const month2 = new Date(end).getMonth();
  const year1 = new Date(start).getFullYear();
  const year2 = new Date(end).getFullYear();
  return month1 === month2 && year1 === year2;
};

/**
 * Returns true if two ISO date strings fall in the same calendar year.
 */
export const isSameYear = (start: string, end: string): boolean => {
  const year1 = new Date(start).getFullYear();
  const year2 = new Date(end).getFullYear();
  return year1 === year2;
};

/**
 * Displays a formatted release date range following the getFastTrackDate pattern:
 * - No dates → "No timeframe yet"
 * - Start only → "Starting in {long month year}"
 * - Same month range → "{long month year}"
 * - Different months, same year → "{short month} - {short month year}"
 * - Different months, different years → "{short month year} - {short month year}"
 */
export const DateRangeInfo: FunctionComponent<DateRangeInfoProps> = ({
  startDate,
  endDate,
  status,
}) => {
  // For Generally Available status, show only the end date
  if (status === 'GENERALLY_AVAILABLE' && endDate) {
    return (
      <Text as="span">
        <FormattedDate value={endDate} month="long" year="numeric" />
      </Text>
    );
  }

  if (!startDate) {
    return (
      <Text as="span">
        <FormattedMessage {...messages.nullReleaseDate} />
      </Text>
    );
  }

  // Start date only — no end date known yet
  if (!endDate) {
    return (
      <Text as="span">
        <FormattedMessage
          {...messages.startingIn}
          values={{
            startingDate: (
              <FormattedDate value={startDate} month="long" year="numeric" />
            ),
          }}
        />
      </Text>
    );
  }

  // Start + end in the same month — show just the month and year
  if (inSameMonth(startDate, endDate)) {
    return (
      <Text as="span">
        <FormattedDate value={startDate} month="long" year="numeric" />
      </Text>
    );
  }

  // Different months — show a range; omit year from start if both are in the same year
  return (
    <Text as="span">
      <FormattedMessage
        {...messages.dateRange}
        values={{
          dateStart: (
            <FormattedDate
              value={startDate}
              month="short"
              year={isSameYear(startDate, endDate) ? undefined : 'numeric'}
            />
          ),
          dateEnd: (
            <FormattedDate value={endDate} month="short" year="numeric" />
          ),
        }}
      />
    </Text>
  );
};

export default DateRangeInfo;
